import { inject, Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, Router, RouterStateSnapshot } from '@angular/router';
import { CacheService } from 'uServices/cache.service';
import { StorageService } from 'uServices/storage.service';
import { BehaviorSubject, catchError, delay, finalize, from, iif, map, Observable, take, throwError } from 'rxjs';
import { Constants } from 'uApp/app.constants';
import { FakeService } from 'uServices/fake.service';
import { ApiService } from 'uServices/api.service';
import { ToastService } from 'uServices/toast.service';
import { TranslateService } from '@ngx-translate/core';
import { SpinnerService } from 'uServices/spinner.service';
import { EventsService } from 'uServices/events.service';
import { UtilsService } from 'uServices/utils.service';
import { NavController } from '@ionic/angular';
import { AlertController } from '@ionic/angular';

const TOKEN_KEY = 'authToken';

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private alertController = inject(AlertController);
  private api = inject(ApiService);
  private cacheSrv = inject(CacheService);
  private events = inject(EventsService);
  private fakeSrv = inject(FakeService);
  private navCtrl = inject(NavController);
  private router = inject(Router);
  private spinner = inject(SpinnerService);
  private storage = inject(StorageService);
  private toast = inject(ToastService);
  private translate = inject(TranslateService);
  private utils = inject(UtilsService);

  public fakeOn: boolean = Constants.FAKE_DATA_ON;
  public alertLogoutShown = false;
  public localeValues: any = {};
  private isLoggedIn = false;
  private authToken!: string;
  private isFirstLoad = true;
  isAuthenticated: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

  constructor() {
    this.loadTranslates();
    this.loadToken();
  }

  async loadToken() {
    const token = await this.storage.getStorageKeyPromise(TOKEN_KEY);
    if (!!token) {
      console.log('AuthenticationService: loadToken - token: ', token);
      this.cacheSrv.setData('app-token', token);
      this.authToken = token.value;
      this.isAuthenticated.next(true);
    } else {
      this.isAuthenticated.next(false);
    }
  }

  // login(): void {
  //   console.log('AuthService: set isLoggedIn - ', this.isLoggedIn);
  //   this.isLoggedIn = true;
  // }

  logout(): void {
    this.authToken = '';
    this.storage.storeKey(TOKEN_KEY, '');
    this.cacheSrv.setData('app-token', '');
    this.isLoggedIn = false;
    this.isAuthenticated.next(false);
  }

  canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): boolean {
    console.log('AuthService: canActivate - isLoggedIn - ', this.isLoggedIn);
    return this.isLoggedIn;
  }

  async canActivatePr(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Promise<boolean> {
    return new Promise<boolean>(async (resolve) => {
      if (this.isFirstLoad) {
        this.isFirstLoad = false;
        await this.loadFromStorage();
      }
      console.log('AuthService: canActivate - isLoggedIn - ', this.isLoggedIn);
      resolve(this.isLoggedIn);
    });
  }

  public getAuthToken(): Promise<string> | string {
    if (this.authToken === undefined) {
      console.log('AuthService: getAuthToken - no urlToken in service: get in storage');
      return this.loadFromStorage();
    } else {
      return this.authToken;
    }
  }

  public getAuthTokenPr(): Promise<string> {
    if (this.authToken === undefined) {
      console.log('AuthService: getAuthTokenPr - no urlToken in service: get in storage');
      return this.loadFromStorage();
    } else {
      return new Promise<string>(resolve => resolve(this.authToken));
    }
  }

  public loadFromStorage() {
    return this.storage.getStorageKeyPromise('authToken').then((token) => {
      console.log('AuthService: 0 loadFromStorage - authToken in storage: ', token);
      this.authToken = token ?? '';
      if (this.authToken !== '') { this.isLoggedIn = true; }
      return this.authToken;
    });
  }

  public getOtp(phone: string): Observable<any> {
    const request: any = { body:{} };
    const requestMethod = Constants.API_METHOD_GET_TEMPTOKEN;
    request.body[requestMethod] = {
      phoneNumber: phone //.replace(/[^0-9]+/g, '')
      //userType: 'Patient'
    };

    let fake = this.fakeOn;
    // fake = true;
    const mapFake = (response: any) => iif(() => fake,
      from(this.fakeSrv.getFakeData('getTempToken')).pipe(delay(500)), response);
    // getTempToken, getTempToken_err4020

    return this.api.runRequest({
      method: Constants.API_URL_GET_TEMPTOKEN,
      request, spinner: true, includeToken: false, includeSystem: true, includeCaptcha: true
    }).pipe(
      // catchError(error => of(error)),
      catchError((error) => {
        const errorN = this.handleError(error, requestMethod, false);
        return throwError(() => errorN);
      }),
      mapFake,
      map(response => {
        console.log('AuthenticationService: GetOTP response:', response);
        if (response && response.hasOwnProperty(requestMethod) && response[requestMethod].error.errorCode === '0') {
          const tempToken = response[requestMethod].token;
          if (tempToken) {
            return tempToken;
          } else {
            const message = this.translate.instant('Api_Handler_error.noTempToken');
            this.toast.showToast({ message });
            throw({ errorCode: false, errorMessage: message });
          }
        } else {
          const error = this.handleError(response, requestMethod, true);
          throw (error);
        }
      }),
      finalize(() => this.spinner.hideSpinner())
    );
  }

  public login(otpCode: number, tempToken: string): Observable<any> {
    const request = {body: {} as any};
    const requestMethod = Constants.API_METHOD_LOGIN;
    request.body[requestMethod] = {
      token: tempToken,
      otp: otpCode
    };

    let fake = this.fakeOn;
    //fake = true;
    const mapFake = (response: any) => iif(() => fake,
      from(this.fakeSrv.getFakeData('authorization')).pipe(delay(500)), response);

    return this.api.runRequest({
      method: Constants.API_URL_GETTOKEN,
      request, spinner: true, includeToken: false, includeSystem: true, includeCaptcha: true
    }).pipe(
      catchError((error) => {
        const errorN = this.handleError(error, requestMethod, false);
        return throwError(() => errorN);
      }),
      mapFake,
      map(response => {
        console.log('AuthenticationService: LoginByOTP response:', response);
        if (response && response.hasOwnProperty(requestMethod) && response[requestMethod].error.errorCode === '0') {
          const token = response[requestMethod].token;
          if (token) {
            console.log('AuthenticationService: LoginByOTP token:', token);
            this.authToken = token;
            this.cacheSrv.setData('app-token', token);
            this.storage.storeKey(TOKEN_KEY, token);
            this.isAuthenticated.next(true);
            this.isLoggedIn = true;
            return true;
          } else {
            const message = this.translate.instant('Api_Handler_error.noToken');
            this.toast.showToast({ message });
            throw false;
          }
        } else {
          const errorData = this.handleError(response, requestMethod);
          throw(errorData);
        }
      }),
      finalize(() => { this.spinner.hideSpinner(); })
    );
  }


  public handleError(errorData: any = {}, requestMethod = '', showToast=true): {errorMessage: string; errorCode: string} {
    console.log('AuthenticationService: handleError - errorData: ', errorData);

    let message;
    const error = errorData && errorData[requestMethod] && errorData[requestMethod].error;
    message = error && error.errorMessage;
    if (!message) { message = errorData && errorData[requestMethod]?.ResponseText; }
    if (!message) { message = errorData && errorData.error?.errorMessage; }
    if (!message) { message = errorData && errorData.errorMessage; }
    let errorCode = error && error.errorCode;
    if (!errorCode) { errorCode = errorData?.errorCode; }
    if (!message && errorCode) { message = 'Error: code - ' + errorCode; }
    if (!message) { message = this.localeValues.default; }
    if (!message) { message = 'Unknown Error'; }
    const skipCodes = '4020,4023,4029,4030,4031,4521,4524';
    const canShowToast = skipCodes.indexOf(errorCode) === -1;
    // 4020 User Not Found
    // 4023 (Перевищено ліміт відправки OTP)
    // 4029 (Термін дії OTP закінчився)
    // 4030 (Перевищено ліміт введення OTP)
    // 4031 (Помилковий OTP)

    // const error = errorData[requestMethod].error;
    // const message = error.errorMessage || this.translate.instant('Api_Handler_error.default');
    console.log('AuthenticationService: handleError - message: ', message);
    if (canShowToast && message && showToast) {
      this.toast.showToast({ message });
    }
    return {errorCode, errorMessage: message};
  }

  listenForLogout(): void {
    this.events.subscribe('app:logout', async (param) => {
      console.log('AuthenticationService:  event Logout 1, param:', param);
      this.utils.closeAllModal();
      this.utils.closeAllPopover();
      const data = param && JSON.parse(param);
      const type = data && data.type;
      if (type === 'endSession') {
        console.log('AuthenticationService: event Logout - endSession');
        this.logout();
        let url: string = this.router.url;
        // console.log('AuthenticationService: event Logout - listenForLogout - url:', url);
        const notInApp = url === '/login' || url === '/start' || url === '/identification';
        if (!this.alertLogoutShown && !notInApp) {
          this.alertLogoutShown = true;
          const message = data.message || this.localeValues.endSession;
          const buttons = [
            {
              text: this.localeValues.ok,
              handler: () => {
                this.navCtrl.navigateRoot('/login');
                  //.then(() => { this.window.location.reload(); });
              }
            }];
          const alert = await this.alertController.create({
            cssClass: 'my-simple-alert', mode: 'ios', backdropDismiss: false, message, buttons});
          alert.onDidDismiss().then(() => this.alertLogoutShown = false);
          alert.present().then(() => this.alertLogoutShown = true);
        }
      }
    });
  }

  private loadTranslates() {
    this.translate.stream('Api_Handler_error')
      .pipe(take(1))
      .subscribe((localeValues) => this.localeValues = localeValues);
  }
}
